// Rehber kaydı: ekran anahtarı → rehber içeriği. Her ekranın içeriği src/guides/<alan>/ altında ayrı dosyadadır.
// Yeni ekran: içerik dosyasını yazın, buraya ekleyin, ekranın başlığına <GuideButton guideKey="..." /> koyun.
// Metinler varsayılan olarak 'guides' çeviri alanındadır (locales/tr|en/guides.json).
import type { TFunction } from 'i18next';
import type { GuideContent } from '../components/common/UsageGuideDialog';
import { addAccountGuide } from './accounting/addAccount';
import { addInventoryItemGuide } from './accounting/addInventoryItem';
import { accountsListGuide } from './accounting/accountsList';
import { accountDetailGuide } from './accounting/accountDetail';
import { accountTransactionsGuide } from './accounting/accountTransactions';
import { accountTransactionDetailGuide } from './accounting/accountTransactionDetail';
import { accountTransactionLogsGuide } from './accounting/accountTransactionLogs';
import { accountMovementsReportGuide } from './accounting/accountMovementsReport';
import { accountMovementsTableSettingsGuide } from './accounting/accountMovementsTableSettings';
import { accountGroupsGuide } from './accounting/accountGroups';
import { accountsTableSettingsGuide } from './accounting/accountsTableSettings';
import { accountBulkAddGuide } from './accounting/accountBulkAdd';
import { accountsPassiveGuide } from './accounting/accountsPassive';
import { accountCollectPaymentGuide } from './accounting/accountCollectPayment';
import { accountMakePaymentGuide } from './accounting/accountMakePayment';
import { accountDebitGuide } from './accounting/accountDebit';
import { accountPaymentReceiptGuide } from './accounting/accountPaymentReceipt';
import { accountShippingAddressesGuide } from './accounting/accountShippingAddresses';
import { inventoryListGuide } from './inventory/inventoryList';
import { inventoryTableSettingsGuide } from './inventory/inventoryTableSettings';
import { priceChangeLogsGuide } from './inventory/priceChangeLogs';
import { stockMovementsReportGuide } from './inventory/stockMovementsReport';
import { marketplaceLogsGuide } from './inventory/marketplaceLogs';
import { inventoryBrandsGuide } from './inventory/inventoryBrands';
import { inventoryCategoriesGuide } from './inventory/inventoryCategories';
import { inventoryWarehousesGuide } from './inventory/inventoryWarehouses';
import { inventoryShipmentTemplatesGuide } from './inventory/shipmentTemplates';
import { inventoryImportGuide } from './inventory/inventoryImport';
import { inventoryBranchPriceGuide } from './inventory/branchPrice';
import { inventoryProductMenusGuide } from './inventory/productMenus';
import { inventoryRecipeGuide } from './inventory/recipe';
import { inventoryStockMovementGuide } from './inventory/stockMovement';
import { inventoryN11UploadGuide } from './inventory/n11Upload';
import { projectsGuide } from './projects/projectsGuide';
import { taskStatusGuide } from './dashboard/taskStatus';
import { weeklyTaskStatusGuide } from './dashboard/weeklyTaskStatus';
import { monthlyTaskStatusGuide } from './dashboard/monthlyTaskStatus';
import { personnelPerformanceGuide } from './dashboard/personnelPerformance';
import { weeklyPersonnelPerformanceGuide } from './dashboard/weeklyPersonnelPerformance';
import { monthlyPersonnelPerformanceGuide } from './dashboard/monthlyPersonnelPerformance';
import { normalTaskStatusGuide } from './dashboard/normalTaskStatus';
import { normalTaskPersonnelPerformanceGuide } from './dashboard/normalTaskPersonnelPerformance';
import { surveySatisfactionGuide } from './dashboard/surveySatisfaction';
import { surveyTrendGuide } from './dashboard/surveyTrend';
import { surveyDetailedStatsGuide } from './dashboard/surveyDetailedStats';
import { personnelListGuide } from './personnel/personnelList';
import { tasksListGuide } from './tasks/tasksList';
import { shiftsListGuide } from './shifts/shiftsList';
import { surveysListGuide } from './surveys/surveysList';
import { bankAccountTransferGuide } from './finance/accountTransfer';
import { addBankAccountGuide } from './finance/addBankAccount';
import { addCheckBookGuide } from './finance/addCheckBook';
import { addCheckNoteGuide } from './finance/addCheckNote';
import { addCreditCardGuide } from './finance/addCreditCard';
import { addLoanGuide } from './finance/addLoan';
import { addBankSubAccountGuide } from './finance/addSubAccount';
import { adminDashboardGuide } from './finance/adminDashboard';
import { bankGuide } from './finance/bank';
import { bankToBankTransferGuide } from './finance/bankToBankTransfer';
import { bankToCashTransferGuide } from './finance/bankToCashTransfer';
import { bankTransactionReportGuide } from './finance/bankTransactionReport';
import { batchPromissoryNoteGuide } from './finance/batchPromissoryNote';
import { cashExpenseGuide } from './finance/cashExpense';
import { cashboxGuide } from './finance/cashbox';
import { cashboxBankTransferGuide } from './finance/cashboxBankTransfer';
import { cashboxTransactionsGuide } from './finance/cashboxTransactions';
import { cashboxTransferGuide } from './finance/cashboxTransfer';
import { changeCheckStatusGuide } from './finance/changeCheckStatus';
import { checkBooksGuide } from './finance/checkBooks';
import { checksNotesGuide } from './finance/checksNotes';
import { creditCardExpenseGuide } from './finance/creditCardExpense';
import { creditCardPaymentGuide } from './finance/creditCardPayment';
import { creditCardTransactionsGuide } from './finance/creditCardTransactions';
import { currencyGuide } from './finance/currency';
import { loanInstallmentPayGuide } from './finance/loanInstallmentPay';
import { loanPaymentsGuide } from './finance/loanPayments';
import { loansGuide } from './finance/loans';
import { profitLossGuide } from './finance/profitLoss';
import { reportBuilderGuide } from './finance/reportBuilder';
import { transactionPaymentMethodChangeGuide } from './finance/transactionPaymentMethodChange';
import { copyItemsGuide } from './documents/copyItems';
import { eInvoiceGuide } from './documents/eInvoice';
import { eInvoiceAccountMatchGuide } from './documents/eInvoiceAccountMatch';
import { eInvoiceImportGuide } from './documents/eInvoiceImport';
import { eInvoiceIntegrationGuide } from './documents/eInvoiceIntegration';
import { eInvoiceSendGuide } from './documents/eInvoiceSend';
import { incomingOrderDetailGuide } from './documents/incomingOrderDetail';
import { interCompanyGuide } from './documents/interCompany';
import { invoiceReportsGuide } from './documents/invoiceReports';
import { invoicesGuide } from './documents/invoices';
import { mailReportsGuide } from './documents/mailReports';
import { mailScheduleGuide } from './documents/mailSchedule';
import { offerTemplateEditorGuide } from './documents/offerTemplateEditor';
import { offersGuide } from './documents/offers';
import { ordersPageGuide } from './documents/ordersPage';
import { pavoPayIntegrationsGuide } from './documents/pavoPayIntegrations';
import { pdfInvoiceImportGuide } from './documents/pdfInvoiceImport';
import { purchaseInvoiceGuide } from './documents/purchaseInvoice';
import { salesInvoiceGuide } from './documents/salesInvoice';
import { waybillsGuide } from './documents/waybills';
import { xsltTemplatesGuide } from './documents/xsltTemplates';
import { branchPricesPageGuide } from './inventory/branchPricesPage';
import { inventoryBulkImageAssignGuide } from './inventory/bulkImageAssign';
import { catalogPickerGuide } from './inventory/catalogPicker';
import { inventoryImageLibraryGuide } from './inventory/imageLibrary';
import { inventoryItemDetailGuide } from './inventory/itemDetail';
import { inventoryLabelPrintingGuide } from './inventory/labelPrinting';
import { inventoryLotsGuide } from './inventory/lots';
import { inventoryN11CategorySelectionGuide } from './inventory/n11CategorySelection';
import { inventoryN11ProductCreateGuide } from './inventory/n11ProductCreate';
import { inventoryProductMenusPageGuide } from './inventory/productMenusPage';
import { productRecipesPageGuide } from './inventory/productRecipesPage';
import { inventoryQuickAddGuide } from './inventory/quickAdd';
import { inventoryScaleSyncGuide } from './inventory/scaleSync';
import { inventoryVariantDictionaryGuide } from './inventory/variantDictionary';
import { inventoryVariantManagementGuide } from './inventory/variantManagement';
import { warehousePageGuide } from './inventory/warehousePage';
import { warehouseTransferGuide } from './inventory/warehouseTransfer';
import { addKioskGuide } from './sales/addKiosk';
import { addPackageCustomerGuide } from './sales/addPackageCustomer';
import { allOrdersGuide } from './sales/allOrders';
import { salesCardReturnGuide } from './sales/cardReturn';
import { salesClubEntryReservationsGuide } from './sales/clubEntryReservations';
import { customerImportGuide } from './sales/customerImport';
import { endOfDayGuide } from './sales/endOfDay';
import { endOfDayListGuide } from './sales/endOfDayList';
import { fiscalWarningGuide } from './sales/fiscalWarning';
import { kioskMenuPageGuide } from './sales/kioskMenuPage';
import { kioskProductsGuide } from './sales/kioskProducts';
import { kioskReceiptSettingsGuide } from './sales/kioskReceiptSettings';
import { kioskSettingsGuide } from './sales/kioskSettings';
import { salesKitchenOrdersGuide } from './sales/kitchenOrders';
import { salesKitchenScreenGuide } from './sales/kitchenScreen';
import { orderManagementPageGuide } from './sales/orderManagementPage';
import { packageOrderScreenGuide } from './sales/packageOrderScreen';
import { packageSettingsGuide } from './sales/packageSettings';
import { partialPaymentGuide } from './sales/partialPayment';
import { salesPavoPayRefundGuide } from './sales/pavoPayRefund';
import { paymentVerificationGuide } from './sales/paymentVerification';
import { salesPoleDisplayGuide } from './sales/poleDisplay';
import { posPaymentErrorGuide } from './sales/posPaymentError';
import { salesPostSaleCardAssignmentGuide } from './sales/postSaleCardAssignment';
import { salesPykScreenGuide } from './sales/pykScreen';
import { salesPykSettingsGuide } from './sales/pykSettings';
import { qrDailyClosingGuide } from './sales/qrDailyClosing';
import { qrMenuPageGuide } from './sales/qrMenuPage';
import { qrMenuSettingsGuide } from './sales/qrMenuSettings';
import { qrOrdersGuide } from './sales/qrOrders';
import { qrReceiptSettingsGuide } from './sales/qrReceiptSettings';
import { qrTablesGuide } from './sales/qrTables';
import { quickSalePageGuide } from './sales/quickSalePage';
import { quickSaleScreenGuide } from './sales/quickSaleScreen';
import { receiptSettingsGuide } from './sales/receiptSettings';
import { salesRestaurantPageGuide } from './sales/restaurantPage';
import { salesRestaurantReportGuide } from './sales/restaurantReport';
import { salesRestaurantScreenGuide } from './sales/restaurantScreen';
import { screenSettingsGuide } from './sales/screenSettings';
import { salesSplitCartGuide } from './sales/splitCart';
import { salesTransferItemGuide } from './sales/transferItem';
import { uiSettingsGuide } from './sales/uiSettings';
import { aiChatWindowGuide } from './aichat/aiChatWindow';
import { createTeamGuide } from './tasks/createTeam';
import { addCardGuide } from './cards/addCard';
import { sidebarSettingsGuide } from './layout/sidebarSettings';
import { addCourierGuide } from './couriers/addCourier';
import { courierOrderSettingsGuide } from './couriers/courierOrderSettings';
import { mediaManagementGuide } from './devices/mediaManagement';
import { createTeamTaskGuide } from './tasks/createTeamTask';
import { qrPrintGuide } from './tasks/qrPrint';
import { shiftQrPrintGuide } from './shifts/shiftQrPrint';
import { shiftTimeEditGuide } from './shifts/shiftTimeEdit';
import { branchCompanyDetailsGuide } from './branches/companyDetails';
import { eventsCalendarGuide } from './events/eventsCalendar';
import { taskReportsGuide } from './tasks/taskReports';
import { cardSessionsGuide } from './cards/cardSessions';
import { usersListGuide } from './users/usersList';
import { courierTrackingGuide } from './couriers/courierTracking';
import { courierManagementGuide } from './couriers/courierManagement';
import { reservationScheduleGuide } from './reservations/reservationSchedule';
import { reservationRequestsGuide } from './reservations/reservationRequests';
import { reservationsPageGuide } from './reservations/reservationsPage';
import { branchesListGuide } from './branches/branchesList';
import { devicesManagementGuide } from './devices/deviceManagement';
import { devicesScalesGuide } from './devices/scaleManagement';
import { cardsAccessGuide } from './cards/accessManagement';
import { cardsListGuide } from './cards/cardsList';
import { cardsPairingGuide } from './cards/cardPairing';
import { pdksManagementGuide } from './pdks/pdksManagement';
import { personnelActionsGuide } from './personnel/personnelActions';
import { personnelSalaryBonusGuide } from './personnel/salaryBonus';
import { personnelSalaryPaymentGuide } from './personnel/salaryPayment';
import { personnelReceivableGuide } from './personnel/personnelReceivable';
import { personnelDebtGuide } from './personnel/personnelDebt';
import { tasksAddTaskGuide } from './tasks/addTask';
import { branchesManagerPermissionsGuide } from './branches/managerPermissions';
import { billingCasesGuide } from './superadmin/billingCases';
import { catalogImportGuide } from './superadmin/catalogImport';
import { dealerNoticesGuide } from './superadmin/dealerNotices';
import { superAdminDealersGuide } from './superadmin/dealers';
import { superAdminLiveSupportGuide } from './superadmin/liveSupport';
import { billingIssuersGuide } from './superadmin/billingIssuers';
import { superAdminTaskMigrationGuide } from './superadmin/taskMigration';
import { superAdminUserMigrationGuide } from './superadmin/userMigration';
import { middlewareMonitorGuide } from './superadmin/middlewareMonitor';
import { superAdminPanelGuide } from './superadmin/panel';
import { superAdminPermissionMigrationGuide } from './superadmin/permissionMigration';
import { catalogProductGuide } from './superadmin/catalogProduct';
import { moduleSettingsGuide } from './superadmin/moduleSettings';
import { integrationsOverviewGuide } from './superadmin/integrationsOverview';
import { billingProductsGuide } from './superadmin/billingProducts';
import { invoicePaperPreviewGuide } from './superadmin/invoicePaperPreview';
import { mailReportsCompanyAccessGuide } from './superadmin/mailReportsCompanyAccess';
import { ecommerceHomePageGuide } from './ecommerce/homePage';
import { ecommerceB2bGuide } from './ecommerce/b2b';
import { ecommercePageGuide } from './ecommerce/ecommercePage';
import { ecommerceHaciogluMappingGuide } from './ecommerce/haciogluMapping';
import { ecommerceCampaignsGuide } from './ecommerce/campaigns';
import { ecommerceReturnsGuide } from './ecommerce/returns';
import { integrationsPageGuide } from './integrations/integrationPage';
import { integrationsPlatformsGuide } from './integrations/platformIntegrations';
import { ecommerceFooterGuide } from './ecommerce/footerSettings';
import { ecommerceBlogGuide } from './ecommerce/blogSettings';
import { ecommerceSupportTicketsGuide } from './ecommerce/supportTickets';
import { ecommerceFormsGuide } from './ecommerce/formsSettings';
import { ecommerceAboutGuide } from './ecommerce/aboutSettings';
import { ecommerceServicesGuide } from './ecommerce/servicesSettings';
import { ecommercePagesGuide } from './ecommerce/pagesSettings';
import { ecommerceSliderGuide } from './ecommerce/sliderSettings';
import { ecommerceShowcaseAreasGuide } from './ecommerce/showcaseAreas';
import { ecommerceLegalDocumentsGuide } from './ecommerce/legalDocuments';
import { ecommerceProductQuestionsGuide } from './ecommerce/productQuestions';
import { ecommerceProductSolutionsGuide } from './ecommerce/productSolutionSettings';
import { ecommerceMembersGuide } from './ecommerce/members';
import { ecommerceBranchesGuide } from './ecommerce/branchSettings';
import { ecommerceProductDisplayGuide } from './ecommerce/productDisplaySettings';
import { productionWorkshopGuide } from './production/workshop';
import { productionPlanningGuide } from './production/planning';
import { productionOrderEditorGuide } from './production/orderEditor';
import { productionOrdersGuide } from './production/orders';
import { productionReportsGuide } from './production/reports';
import { productionTreeGuide } from './production/tree';
import { productionCreateOrdersFromPlanGuide } from './production/createOrdersFromPlan';
import { productionConfirmStepGuide } from './production/confirmStep';
import { productionDefinitionsGuide } from './production/definitions';
import { productionOperationFormGuide } from './production/operationForm';
import { productionWorkCenterFormGuide } from './production/workCenterForm';
import { qrMenuSelectionGuide } from './sales/qrMenuSelection';
import { bulkWarehouseAssignGuide } from './inventory/bulkWarehouseAssign';
import { einvoiceUserQueryGuide } from './documents/einvoiceUserQuery';
import { addIntegrationModalGuide } from './ecommerce/addIntegrationModal';
import { editIntegrationModalGuide } from './ecommerce/editIntegrationModal';
import { marketplaceProductsModalGuide } from './ecommerce/marketplaceProductsModal';
import { trendyolWebhookSettingsGuide } from './ecommerce/trendyolWebhookSettings';
import { productMappingModalGuide } from './ecommerce/productMappingModal';
import { contentCardsDialogGuide } from './ecommerce/contentCardsDialog';
import { paymentIntegrationsSectionGuide } from './integrations/paymentIntegrationsSection';
import { addPaymentIntegrationModalGuide } from './integrations/addPaymentIntegrationModal';
import { editPaymentIntegrationModalGuide } from './integrations/editPaymentIntegrationModal';
import { mokaPaymentSettingsGuide } from './integrations/mokaPaymentSettings';
import { wmsMapEditorGuide } from './wms/mapEditor';
import { wmsWarehouseSetupGuide } from './wms/warehouseSetup';
import { wmsLocationManagerGuide } from './wms/locationManager';
import { wmsReceivingScanGuide } from './wms/receivingScan';
import { wmsReceivingManagementGuide } from './wms/receivingManagement';
import { wmsOrderPoolGuide } from './wms/orderPool';
import { wmsCycleCountGuide } from './wms/cycleCount';
import { wmsPickingGuide } from './wms/picking';
import { wmsPickingConsolidatedGuide } from './wms/pickingConsolidated';
import { wmsReportsGuide } from './wms/reports';
import { wmsCancelManagementGuide } from './wms/cancelManagement';
import { wmsWavesGuide } from './wms/waves';
import { wmsWaveSetupWizardGuide } from './wms/waveSetupWizard';
import { wmsEquipmentGuide } from './wms/equipment';
import { wmsScreenSetupGuide } from './wms/screenSetup';
import { wmsDashboardGuide } from './wms/dashboard';
import { wmsSmartWarehouseGuide } from './wms/smartWarehouse';
import { wmsCreateOrderGuide } from './wms/createOrder';
import { productionFixedOrderFormGuide } from './production/fixedOrderForm';
import { productionFixedProduceGuide } from './production/fixedProduce';

export interface GuideEntry {
  /** Metinlerin çeviri alanı. */
  ns: string;
  build: (t: TFunction) => GuideContent;
}

const defineGuides = <T extends Record<string, GuideEntry>>(guides: T): T => guides;

export const GUIDES = defineGuides({
  // Kartlar (metinler 'accounting' alanında)
  'accounts.add': { ns: 'accounting', build: addAccountGuide },
  'inventory.add': { ns: 'accounting', build: addInventoryItemGuide },

  // Cari (Plan 1 R1)
  'accounts.list': { ns: 'guides', build: accountsListGuide },
  'accounts.detail': { ns: 'guides', build: accountDetailGuide },
  'accounts.transactions': { ns: 'guides', build: accountTransactionsGuide },
  'accounts.transactionDetail': { ns: 'guides', build: accountTransactionDetailGuide },
  'accounts.transactionLogs': { ns: 'guides', build: accountTransactionLogsGuide },
  'accounts.movementsReport': { ns: 'guides', build: accountMovementsReportGuide },
  'accounts.movementsReport.tableSettings': { ns: 'guides', build: accountMovementsTableSettingsGuide },
  'accounts.groups': { ns: 'guides', build: accountGroupsGuide },
  'accounts.tableSettings': { ns: 'guides', build: accountsTableSettingsGuide },
  'accounts.bulkAdd': { ns: 'guides', build: accountBulkAddGuide },
  'accounts.passive': { ns: 'guides', build: accountsPassiveGuide },
  'accounts.collectPayment': { ns: 'guides', build: accountCollectPaymentGuide },
  'accounts.makePayment': { ns: 'guides', build: accountMakePaymentGuide },
  'accounts.debit': { ns: 'guides', build: accountDebitGuide },
  'accounts.paymentReceipt': { ns: 'guides', build: accountPaymentReceiptGuide },
  'accounts.shippingAddresses': { ns: 'guides', build: accountShippingAddressesGuide },

  // Stok (Plan 1 R2)
  'inventory.list': { ns: 'guides', build: inventoryListGuide },
  'inventory.tableSettings': { ns: 'guides', build: inventoryTableSettingsGuide },
  'inventory.priceChangeLogs': { ns: 'guides', build: priceChangeLogsGuide },
  'inventory.stockMovementsReport': { ns: 'guides', build: stockMovementsReportGuide },
  'inventory.marketplaceLogs': { ns: 'guides', build: marketplaceLogsGuide },
  'inventory.brands': { ns: 'guides', build: inventoryBrandsGuide },
  'inventory.categories': { ns: 'guides', build: inventoryCategoriesGuide },
  'inventory.warehouses': { ns: 'guides', build: inventoryWarehousesGuide },
  'inventory.shipmentTemplates': { ns: 'guides', build: inventoryShipmentTemplatesGuide },
  'inventory.import': { ns: 'guides', build: inventoryImportGuide },
  'inventory.branchPrice': { ns: 'guides', build: inventoryBranchPriceGuide },
  'inventory.productMenus': { ns: 'guides', build: inventoryProductMenusGuide },
  'inventory.recipe': { ns: 'guides', build: inventoryRecipeGuide },
  'inventory.stockMovement': { ns: 'guides', build: inventoryStockMovementGuide },
  'inventory.n11Upload': { ns: 'guides', build: inventoryN11UploadGuide },

  // Projeler (Plan 1 R3)
  'projects.guide': { ns: 'guides', build: projectsGuide },

  // Dashboard grafikleri (Plan 1 R4)
  'dashboard.taskStatus': { ns: 'guides', build: taskStatusGuide },
  'dashboard.weeklyTaskStatus': { ns: 'guides', build: weeklyTaskStatusGuide },
  'dashboard.monthlyTaskStatus': { ns: 'guides', build: monthlyTaskStatusGuide },
  'dashboard.personnelPerformance': { ns: 'guides', build: personnelPerformanceGuide },
  'dashboard.weeklyPersonnelPerformance': { ns: 'guides', build: weeklyPersonnelPerformanceGuide },
  'dashboard.monthlyPersonnelPerformance': { ns: 'guides', build: monthlyPersonnelPerformanceGuide },
  'dashboard.normalTaskStatus': { ns: 'guides', build: normalTaskStatusGuide },
  'dashboard.normalTaskPersonnelPerformance': { ns: 'guides', build: normalTaskPersonnelPerformanceGuide },
  'dashboard.surveySatisfaction': { ns: 'guides', build: surveySatisfactionGuide },
  'dashboard.surveyTrend': { ns: 'guides', build: surveyTrendGuide },
  'dashboard.surveyDetailedStats': { ns: 'guides', build: surveyDetailedStatsGuide },

  // Diğer sayfalar (Plan 1 R5)
  'personnel.list': { ns: 'guides', build: personnelListGuide },
  'tasks.list': { ns: 'guides', build: tasksListGuide },
  'shifts.list': { ns: 'guides', build: shiftsListGuide },
  'surveys.list': { ns: 'guides', build: surveysListGuide },

  // Finans (Plan 2 E1)
  'finance.accountTransfer': { ns: 'guides', build: bankAccountTransferGuide },
  'finance.addBankAccount': { ns: 'guides', build: addBankAccountGuide },
  'finance.addCheckBook': { ns: 'guides', build: addCheckBookGuide },
  'finance.addCheckNote': { ns: 'guides', build: addCheckNoteGuide },
  'finance.addCreditCard': { ns: 'guides', build: addCreditCardGuide },
  'finance.addLoan': { ns: 'guides', build: addLoanGuide },
  'finance.addSubAccount': { ns: 'guides', build: addBankSubAccountGuide },
  'finance.adminDashboard': { ns: 'guides', build: adminDashboardGuide },
  'finance.bank': { ns: 'guides', build: bankGuide },
  'finance.bankToBankTransfer': { ns: 'guides', build: bankToBankTransferGuide },
  'finance.bankToCashTransfer': { ns: 'guides', build: bankToCashTransferGuide },
  'finance.bankTransactionReport': { ns: 'guides', build: bankTransactionReportGuide },
  'finance.batchPromissoryNote': { ns: 'guides', build: batchPromissoryNoteGuide },
  'finance.cashExpense': { ns: 'guides', build: cashExpenseGuide },
  'finance.cashbox': { ns: 'guides', build: cashboxGuide },
  'finance.cashboxBankTransfer': { ns: 'guides', build: cashboxBankTransferGuide },
  'finance.cashboxTransactions': { ns: 'guides', build: cashboxTransactionsGuide },
  'finance.cashboxTransfer': { ns: 'guides', build: cashboxTransferGuide },
  'finance.changeCheckStatus': { ns: 'guides', build: changeCheckStatusGuide },
  'finance.checkBooks': { ns: 'guides', build: checkBooksGuide },
  'finance.checksNotes': { ns: 'guides', build: checksNotesGuide },
  'finance.creditCardExpense': { ns: 'guides', build: creditCardExpenseGuide },
  'finance.creditCardPayment': { ns: 'guides', build: creditCardPaymentGuide },
  'finance.creditCardTransactions': { ns: 'guides', build: creditCardTransactionsGuide },
  'finance.currency': { ns: 'guides', build: currencyGuide },
  'finance.loanInstallmentPay': { ns: 'guides', build: loanInstallmentPayGuide },
  'finance.loanPayments': { ns: 'guides', build: loanPaymentsGuide },
  'finance.loans': { ns: 'guides', build: loansGuide },
  'finance.profitLoss': { ns: 'guides', build: profitLossGuide },
  'finance.reportBuilder': { ns: 'guides', build: reportBuilderGuide },
  'finance.transactionPaymentMethodChange': { ns: 'guides', build: transactionPaymentMethodChangeGuide },

  // Belgeler (Plan 2 E2)
  'documents.copyItems': { ns: 'guides', build: copyItemsGuide },
  'documents.eInvoice': { ns: 'guides', build: eInvoiceGuide },
  'documents.eInvoiceAccountMatch': { ns: 'guides', build: eInvoiceAccountMatchGuide },
  'documents.eInvoiceImport': { ns: 'guides', build: eInvoiceImportGuide },
  'documents.eInvoiceIntegration': { ns: 'guides', build: eInvoiceIntegrationGuide },
  'documents.eInvoiceSend': { ns: 'guides', build: eInvoiceSendGuide },
  'documents.incomingOrderDetail': { ns: 'guides', build: incomingOrderDetailGuide },
  'documents.interCompany': { ns: 'guides', build: interCompanyGuide },
  'documents.invoiceReports': { ns: 'guides', build: invoiceReportsGuide },
  'documents.invoices': { ns: 'guides', build: invoicesGuide },
  'documents.mailReports': { ns: 'guides', build: mailReportsGuide },
  'documents.mailSchedule': { ns: 'guides', build: mailScheduleGuide },
  'documents.offerTemplateEditor': { ns: 'guides', build: offerTemplateEditorGuide },
  'documents.offers': { ns: 'guides', build: offersGuide },
  'documents.ordersPage': { ns: 'guides', build: ordersPageGuide },
  'documents.pavoPayIntegrations': { ns: 'guides', build: pavoPayIntegrationsGuide },
  'documents.pdfInvoiceImport': { ns: 'guides', build: pdfInvoiceImportGuide },
  'documents.purchaseInvoice': { ns: 'guides', build: purchaseInvoiceGuide },
  'documents.salesInvoice': { ns: 'guides', build: salesInvoiceGuide },
  'documents.waybills': { ns: 'guides', build: waybillsGuide },
  'documents.xsltTemplates': { ns: 'guides', build: xsltTemplatesGuide },

  // Stok ve depo (Plan 2 E3)
  'inventory.branchPricesPage': { ns: 'guides', build: branchPricesPageGuide },
  'inventory.bulkImageAssign': { ns: 'guides', build: inventoryBulkImageAssignGuide },
  'inventory.catalogPicker': { ns: 'guides', build: catalogPickerGuide },
  'inventory.imageLibrary': { ns: 'guides', build: inventoryImageLibraryGuide },
  'inventory.itemDetail': { ns: 'guides', build: inventoryItemDetailGuide },
  'inventory.labelPrinting': { ns: 'guides', build: inventoryLabelPrintingGuide },
  'inventory.lots': { ns: 'guides', build: inventoryLotsGuide },
  'inventory.n11CategorySelection': { ns: 'guides', build: inventoryN11CategorySelectionGuide },
  'inventory.n11ProductCreate': { ns: 'guides', build: inventoryN11ProductCreateGuide },
  'inventory.productMenusPage': { ns: 'guides', build: inventoryProductMenusPageGuide },
  'inventory.productRecipesPage': { ns: 'guides', build: productRecipesPageGuide },
  'inventory.quickAdd': { ns: 'guides', build: inventoryQuickAddGuide },
  'inventory.scaleSync': { ns: 'guides', build: inventoryScaleSyncGuide },
  'inventory.variantDictionary': { ns: 'guides', build: inventoryVariantDictionaryGuide },
  'inventory.variantManagement': { ns: 'guides', build: inventoryVariantManagementGuide },
  'inventory.warehousePage': { ns: 'guides', build: warehousePageGuide },
  'inventory.warehouseTransfer': { ns: 'guides', build: warehouseTransferGuide },

  // Satış sistemi (Plan 2 E4)
  'sales.addKiosk': { ns: 'guides', build: addKioskGuide },
  'sales.addPackageCustomer': { ns: 'guides', build: addPackageCustomerGuide },
  'sales.allOrders': { ns: 'guides', build: allOrdersGuide },
  'sales.cardReturn': { ns: 'guides', build: salesCardReturnGuide },
  'sales.clubEntryReservations': { ns: 'guides', build: salesClubEntryReservationsGuide },
  'sales.customerImport': { ns: 'guides', build: customerImportGuide },
  'sales.endOfDay': { ns: 'guides', build: endOfDayGuide },
  'sales.endOfDayList': { ns: 'guides', build: endOfDayListGuide },
  'sales.fiscalWarning': { ns: 'guides', build: fiscalWarningGuide },
  'sales.kioskMenuPage': { ns: 'guides', build: kioskMenuPageGuide },
  'sales.kioskProducts': { ns: 'guides', build: kioskProductsGuide },
  'sales.kioskReceiptSettings': { ns: 'guides', build: kioskReceiptSettingsGuide },
  'sales.kioskSettings': { ns: 'guides', build: kioskSettingsGuide },
  'sales.kitchenOrders': { ns: 'guides', build: salesKitchenOrdersGuide },
  'sales.kitchenScreen': { ns: 'guides', build: salesKitchenScreenGuide },
  'sales.orderManagementPage': { ns: 'guides', build: orderManagementPageGuide },
  'sales.packageOrderScreen': { ns: 'guides', build: packageOrderScreenGuide },
  'sales.packageSettings': { ns: 'guides', build: packageSettingsGuide },
  'sales.partialPayment': { ns: 'guides', build: partialPaymentGuide },
  'sales.pavoPayRefund': { ns: 'guides', build: salesPavoPayRefundGuide },
  'sales.paymentVerification': { ns: 'guides', build: paymentVerificationGuide },
  'sales.poleDisplay': { ns: 'guides', build: salesPoleDisplayGuide },
  'sales.posPaymentError': { ns: 'guides', build: posPaymentErrorGuide },
  'sales.postSaleCardAssignment': { ns: 'guides', build: salesPostSaleCardAssignmentGuide },
  'sales.pykScreen': { ns: 'guides', build: salesPykScreenGuide },
  'sales.pykSettings': { ns: 'guides', build: salesPykSettingsGuide },
  'sales.qrDailyClosing': { ns: 'guides', build: qrDailyClosingGuide },
  'sales.qrMenuPage': { ns: 'guides', build: qrMenuPageGuide },
  'sales.qrMenuSettings': { ns: 'guides', build: qrMenuSettingsGuide },
  'sales.qrOrders': { ns: 'guides', build: qrOrdersGuide },
  'sales.qrReceiptSettings': { ns: 'guides', build: qrReceiptSettingsGuide },
  'sales.qrTables': { ns: 'guides', build: qrTablesGuide },
  'sales.quickSalePage': { ns: 'guides', build: quickSalePageGuide },
  'sales.quickSaleScreen': { ns: 'guides', build: quickSaleScreenGuide },
  'sales.receiptSettings': { ns: 'guides', build: receiptSettingsGuide },
  'sales.restaurantPage': { ns: 'guides', build: salesRestaurantPageGuide },
  'sales.restaurantReport': { ns: 'guides', build: salesRestaurantReportGuide },
  'sales.restaurantScreen': { ns: 'guides', build: salesRestaurantScreenGuide },
  'sales.screenSettings': { ns: 'guides', build: screenSettingsGuide },
  'sales.splitCart': { ns: 'guides', build: salesSplitCartGuide },
  'sales.transferItem': { ns: 'guides', build: salesTransferItemGuide },
  'sales.uiSettings': { ns: 'guides', build: uiSettingsGuide },
  'aichat.window': { ns: 'guides', build: aiChatWindowGuide },
  'tasks.createTeam': { ns: 'guides', build: createTeamGuide },
  'cards.addCard': { ns: 'guides', build: addCardGuide },
  'layout.sidebarSettings': { ns: 'guides', build: sidebarSettingsGuide },
  'couriers.addCourier': { ns: 'guides', build: addCourierGuide },
  'couriers.orderSettings': { ns: 'guides', build: courierOrderSettingsGuide },
  'devices.media': { ns: 'guides', build: mediaManagementGuide },
  'tasks.createTeamTask': { ns: 'guides', build: createTeamTaskGuide },
  'tasks.qrPrint': { ns: 'guides', build: qrPrintGuide },
  'shifts.qrPrint': { ns: 'guides', build: shiftQrPrintGuide },
  'shifts.timeEdit': { ns: 'guides', build: shiftTimeEditGuide },
  'branches.companyDetails': { ns: 'guides', build: branchCompanyDetailsGuide },
  'events.calendar': { ns: 'guides', build: eventsCalendarGuide },
  'tasks.reports': { ns: 'guides', build: taskReportsGuide },
  'cards.sessions': { ns: 'guides', build: cardSessionsGuide },
  'users.list': { ns: 'guides', build: usersListGuide },
  'couriers.tracking': { ns: 'guides', build: courierTrackingGuide },
  'couriers.management': { ns: 'guides', build: courierManagementGuide },
  'reservations.schedule': { ns: 'guides', build: reservationScheduleGuide },
  'reservations.requests': { ns: 'guides', build: reservationRequestsGuide },
  'reservations.page': { ns: 'guides', build: reservationsPageGuide },
  'branches.list': { ns: 'guides', build: branchesListGuide },
  'devices.management': { ns: 'guides', build: devicesManagementGuide },
  'devices.scales': { ns: 'guides', build: devicesScalesGuide },
  'cards.access': { ns: 'guides', build: cardsAccessGuide },
  'cards.list': { ns: 'guides', build: cardsListGuide },
  'cards.pairing': { ns: 'guides', build: cardsPairingGuide },
  'pdks.management': { ns: 'guides', build: pdksManagementGuide },
  'personnel.actions': { ns: 'guides', build: personnelActionsGuide },
  'personnel.salaryBonus': { ns: 'guides', build: personnelSalaryBonusGuide },
  'personnel.salaryPayment': { ns: 'guides', build: personnelSalaryPaymentGuide },
  'personnel.receivable': { ns: 'guides', build: personnelReceivableGuide },
  'personnel.debt': { ns: 'guides', build: personnelDebtGuide },
  'tasks.addTask': { ns: 'guides', build: tasksAddTaskGuide },
  'branches.managerPermissions': { ns: 'guides', build: branchesManagerPermissionsGuide },
  'superadmin.billingCases': { ns: 'guides', build: billingCasesGuide },
  'superadmin.catalogImport': { ns: 'guides', build: catalogImportGuide },
  'superadmin.dealerNotices': { ns: 'guides', build: dealerNoticesGuide },
  'superadmin.dealers': { ns: 'guides', build: superAdminDealersGuide },
  'superadmin.liveSupport': { ns: 'guides', build: superAdminLiveSupportGuide },
  'superadmin.billingIssuers': { ns: 'guides', build: billingIssuersGuide },
  'superadmin.taskMigration': { ns: 'guides', build: superAdminTaskMigrationGuide },
  'superadmin.userMigration': { ns: 'guides', build: superAdminUserMigrationGuide },
  'superadmin.middlewareMonitor': { ns: 'guides', build: middlewareMonitorGuide },
  'superadmin.panel': { ns: 'guides', build: superAdminPanelGuide },
  'superadmin.permissionMigration': { ns: 'guides', build: superAdminPermissionMigrationGuide },
  'superadmin.catalogProduct': { ns: 'guides', build: catalogProductGuide },
  'superadmin.moduleSettings': { ns: 'guides', build: moduleSettingsGuide },
  'superadmin.integrationsOverview': { ns: 'guides', build: integrationsOverviewGuide },
  'superadmin.billingProducts': { ns: 'guides', build: billingProductsGuide },
  'superadmin.invoicePaperPreview': { ns: 'guides', build: invoicePaperPreviewGuide },
  'superadmin.mailReportsCompanyAccess': { ns: 'guides', build: mailReportsCompanyAccessGuide },
  'ecommerce.homePage': { ns: 'guides', build: ecommerceHomePageGuide },
  'ecommerce.b2b': { ns: 'guides', build: ecommerceB2bGuide },
  'ecommerce.page': { ns: 'guides', build: ecommercePageGuide },
  'ecommerce.haciogluMapping': { ns: 'guides', build: ecommerceHaciogluMappingGuide },
  'ecommerce.campaigns': { ns: 'guides', build: ecommerceCampaignsGuide },
  'ecommerce.returns': { ns: 'guides', build: ecommerceReturnsGuide },
  'integrations.page': { ns: 'guides', build: integrationsPageGuide },
  'integrations.platforms': { ns: 'guides', build: integrationsPlatformsGuide },
  'ecommerce.footer': { ns: 'guides', build: ecommerceFooterGuide },
  'ecommerce.blog': { ns: 'guides', build: ecommerceBlogGuide },
  'ecommerce.supportTickets': { ns: 'guides', build: ecommerceSupportTicketsGuide },
  'ecommerce.forms': { ns: 'guides', build: ecommerceFormsGuide },
  'ecommerce.about': { ns: 'guides', build: ecommerceAboutGuide },
  'ecommerce.services': { ns: 'guides', build: ecommerceServicesGuide },
  'ecommerce.pages': { ns: 'guides', build: ecommercePagesGuide },
  'ecommerce.slider': { ns: 'guides', build: ecommerceSliderGuide },
  'ecommerce.showcaseAreas': { ns: 'guides', build: ecommerceShowcaseAreasGuide },
  'ecommerce.legalDocuments': { ns: 'guides', build: ecommerceLegalDocumentsGuide },
  'ecommerce.productQuestions': { ns: 'guides', build: ecommerceProductQuestionsGuide },
  'ecommerce.productSolutions': { ns: 'guides', build: ecommerceProductSolutionsGuide },
  'ecommerce.members': { ns: 'guides', build: ecommerceMembersGuide },
  'ecommerce.branches': { ns: 'guides', build: ecommerceBranchesGuide },
  'ecommerce.productDisplay': { ns: 'guides', build: ecommerceProductDisplayGuide },
  'production.workshop': { ns: 'guides', build: productionWorkshopGuide },
  'production.planning': { ns: 'guides', build: productionPlanningGuide },
  'production.orderEditor': { ns: 'guides', build: productionOrderEditorGuide },
  'production.orders': { ns: 'guides', build: productionOrdersGuide },
  'production.reports': { ns: 'guides', build: productionReportsGuide },
  'production.tree': { ns: 'guides', build: productionTreeGuide },
  'production.createOrdersFromPlan': { ns: 'guides', build: productionCreateOrdersFromPlanGuide },
  'production.confirmStep': { ns: 'guides', build: productionConfirmStepGuide },
  'production.definitions': { ns: 'guides', build: productionDefinitionsGuide },
  'production.operationForm': { ns: 'guides', build: productionOperationFormGuide },
  'production.workCenterForm': { ns: 'guides', build: productionWorkCenterFormGuide },
  'sales.qrMenuSelection': { ns: 'guides', build: qrMenuSelectionGuide },
  'inventory.bulkWarehouseAssign': { ns: 'guides', build: bulkWarehouseAssignGuide },
  'documents.einvoiceUserQuery': { ns: 'guides', build: einvoiceUserQueryGuide },
  'ecommerce.addIntegration': { ns: 'guides', build: addIntegrationModalGuide },
  'ecommerce.editIntegration': { ns: 'guides', build: editIntegrationModalGuide },
  'ecommerce.marketplaceProducts': { ns: 'guides', build: marketplaceProductsModalGuide },
  'ecommerce.trendyolWebhook': { ns: 'guides', build: trendyolWebhookSettingsGuide },
  'ecommerce.productMapping': { ns: 'guides', build: productMappingModalGuide },
  'ecommerce.contentCards': { ns: 'guides', build: contentCardsDialogGuide },
  'integrations.payments': { ns: 'guides', build: paymentIntegrationsSectionGuide },
  'integrations.addPayment': { ns: 'guides', build: addPaymentIntegrationModalGuide },
  'integrations.editPayment': { ns: 'guides', build: editPaymentIntegrationModalGuide },
  'integrations.mokaSettings': { ns: 'guides', build: mokaPaymentSettingsGuide },
  'wms.mapEditor': { ns: 'guides', build: wmsMapEditorGuide },
  'wms.warehouseSetup': { ns: 'guides', build: wmsWarehouseSetupGuide },
  'wms.locationManager': { ns: 'guides', build: wmsLocationManagerGuide },
  'wms.receivingScan': { ns: 'guides', build: wmsReceivingScanGuide },
  'wms.receivingManagement': { ns: 'guides', build: wmsReceivingManagementGuide },
  'wms.orderPool': { ns: 'guides', build: wmsOrderPoolGuide },
  'wms.cycleCount': { ns: 'guides', build: wmsCycleCountGuide },
  'wms.picking': { ns: 'guides', build: wmsPickingGuide },
  'wms.pickingConsolidated': { ns: 'guides', build: wmsPickingConsolidatedGuide },
  'wms.reports': { ns: 'guides', build: wmsReportsGuide },
  'wms.cancelManagement': { ns: 'guides', build: wmsCancelManagementGuide },
  'wms.waves': { ns: 'guides', build: wmsWavesGuide },
  'wms.waveSetupWizard': { ns: 'guides', build: wmsWaveSetupWizardGuide },
  'wms.equipment': { ns: 'guides', build: wmsEquipmentGuide },
  'wms.screenSetup': { ns: 'guides', build: wmsScreenSetupGuide },
  'wms.dashboard': { ns: 'guides', build: wmsDashboardGuide },
  'wms.smartWarehouse': { ns: 'guides', build: wmsSmartWarehouseGuide },
  'wms.createOrder': { ns: 'guides', build: wmsCreateOrderGuide },
  'production.fixedOrderForm': { ns: 'guides', build: productionFixedOrderFormGuide },
  'production.fixedProduce': { ns: 'guides', build: productionFixedProduceGuide },
});

export type GuideKey = keyof typeof GUIDES;
